import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  BuildingOfficeIcon,
  StarIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  ArrowsRightLeftIcon,
  TableCellsIcon,
  ArrowDownTrayIcon,
  Squares2X2Icon as CardsIcon,
  LightBulbIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/react/24/outline";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/solid";
import * as cadviewer from "cadviewer";
import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { CadviewerContext } from "../../../../context/Cadviewer.Context";
import useConfig from "../../../../demos/hooks/useConfig";
import { SpaceInfo } from "../../../../types/plan";
import { ChatMessage, SearchResultSpaceInfo } from "../../types/aiChatClient";
import { ChatMessageV2 } from "../../types/aiChatClientV2";
import {
  ChatMessageV5,
  RelocationResult,
  RelocationSpaceDetail,
} from "../../types/aiChatClientV5";
import { TenantRecommendation } from "../../types/recommendation";
import { TableData, isSpaceNumberField } from "../../types/table";
import TablePreview from "../TablePreview/TablePreview";
import RelocationCard from "../RelocationCard";
import ReasoningTraceModal from "../ReasoningTraceModal";
import { useSpaceSelectionStore } from "../../stores/spaceSelectionStore";
import { downloadRequestLog } from "../../utils/aiLogUtils";
import {
  createHighlightStyle,
  isVacantTenant,
  highlight_blue,
} from "../../utils/aiChatUtils";
// The PDF exporters pull in jsPDF, pdf-lib and html2canvas — around 800 KB,
// more than a tenth of the main bundle, for a feature that runs only when
// someone asks for a report. Loaded on demand, like exceljs in ../types/table.

// Helper function to get relative time
const getRelativeTime = (timestamp: string): string => {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return past.toLocaleDateString();
};

interface SearchResultCardProps {
  result: SearchResultSpaceInfo;
  onClick: (result: SearchResultSpaceInfo) => void;
  currentPlanName?: string;
  currentPropertyNumber?: string;
  onSwitchPlan?: (planName: string, propertyNumber?: string) => void;
}

interface RecommendationCardProps {
  recommendation: TenantRecommendation;
  onHighlight?: (tenantName: string, coTenants: string[], targetSpaceNumber?: string, targetHighlightColor?: string) => void;
  targetSpaceNumber?: string;
  targetHighlightColor?: string;
}

interface SpaceCombinationSpace {
  space_number: string;
  tenant_name: string;
  area_sf?: number;
  area?: number;
  area_unit?: string;
  total_rent_usd?: number;
  total_rent?: number;
  currency?: string;
  rent_per_sf?: number;
  sales_r12_usd?: number | null;
  occupancy_percentage?: number | null;
  lease_start_date?: string;
  lease_end_date?: string;
  days_until_expiration?: number;
  rent_remaining_usd?: number;
  kickout_option?: string;
  deal_type?: string;
  deal_stage?: string;
  lease_status?: string;
  merchandise_category?: string;
  mall_name?: string;
  plan_name?: string;
  property_number?: string;
  highlight_color?: string;
}

interface SpaceCombination {
  rank?: number;
  combination_id?: string;
  total_area_sf?: number;
  total_area?: number;
  area_unit?: string;
  total_rent_usd?: number;
  total_rent?: number;
  currency?: string;
  earliest_expiration?: string;
  latest_expiration?: string;
  days_until_earliest_expiration?: number;
  total_rent_remaining_usd?: number;
  buyout_options?: string[];
  buyout_cost_usd?: number;
  spaces: SpaceCombinationSpace[];
  scoring_breakdown?: {
    lease_time_score?: number;
    rent_cost_score?: number;
    total_score?: number;
  };
  analysis?: {
    strengths?: string[];
    considerations?: string[];
  };
}

interface CombinationCardProps {
  combination: SpaceCombination;
  onHighlight?: (spaces: SpaceCombinationSpace[]) => void;
  currentPlanName?: string;
  currentPropertyNumber?: string;
  onSwitchPlan?: (planName: string, propertyNumber?: string, spacesToHighlight?: string[]) => void;
}

interface CoTenantsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantName: string;
  coTenantAlignment: {
    overlap_count: number;
    common_neighbors?: string[];
    anchor_brands_nearby?: string[];
  };
}

const CoTenantsModal: React.FC<CoTenantsModalProps> = ({
  isOpen,
  onClose,
  tenantName,
  coTenantAlignment,
}) => {
  if (!isOpen) return null;

  const commonNeighbors = coTenantAlignment.common_neighbors || [];
  const anchorBrands = coTenantAlignment.anchor_brands_nearby || [];

  return (
    <div className="fixed inset-0 z-[1000000002] overflow-y-auto w-full h-full">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4 border-b border-orange-800">
            <div className="flex items-center justify-between">
              <h3 className="text-xl leading-6 font-bold text-white flex items-center gap-2">
                <BuildingOfficeIcon className="h-6 w-6" />
                Co-tenants for {tenantName}
              </h3>
              <button
                onClick={onClose}
                className="text-white hover:text-orange-200 transition-colors"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <p className="mt-1 text-sm text-orange-100">
              {coTenantAlignment.overlap_count} common co-tenants found
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {/* Anchor Brands Section */}
            {anchorBrands.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <StarIcon className="h-5 w-5 text-yellow-500" />
                  Anchor Brands
                </h4>
                <div className="flex flex-wrap gap-2">
                  {anchorBrands.map((brand, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300"
                    >
                      {brand}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* All Co-tenants Section */}
            <div>
              <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                <BuildingOfficeIcon className="h-5 w-5 text-orange-600" />
                All Co-tenants ({commonNeighbors.length})
              </h4>

              {/* Grid view for co-tenants */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {commonNeighbors.map((tenant, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-orange-50 hover:border-orange-300 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="bg-orange-100 p-1.5 rounded-full mr-2 flex-shrink-0">
                        <BuildingOfficeIcon className="h-4 w-4 text-orange-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-800">
                        {tenant}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const highlight_yellow = {
  fill: "#fafa00", //  #FFFCBB yellow
  "fill-opacity": 0.8,
  stroke: "#FFD300", //  orange
  "stroke-width": 2.0,
  "stroke-opacity": 1,
  "stroke-linejoin": "round",
};

export const highlight_red = {
  fill: "#ff0000", // red
  "fill-opacity": 0.8,
  stroke: "#cc0000",
  "stroke-width": 2.0,
  "stroke-opacity": 1,
  "stroke-linejoin": "round",
};

export const highlight_dark_green = {
  fill: "#228B22", // dark green
  "fill-opacity": 0.8,
  stroke: "#228B22",
  "stroke-width": 2.0,
  "stroke-opacity": 1,
  "stroke-linejoin": "round",
};

export const highlight_green = {
  fill: "#32CD32",
  "fill-opacity": 0.8,
  stroke: "#228B22",
  "stroke-width": 2.0,
  "stroke-opacity": 1,
  "stroke-linejoin": "round",
};

const TargetPlanInfoCard: React.FC<{
  spaceInfo: SpaceInfo;
  onClick: () => void;
}> = ({ spaceInfo, onClick }) => (
  <div
    className="flex-none w-58 bg-white border-l-4 border-l-red-500 border-t border-r border-b border-gray-200 rounded-lg shadow-md p-3 cursor-pointer hover:shadow-lg transition-shadow duration-200"
    onClick={() => onClick()}
  >
    <div className="flex items-center">
      <div className="bg-red-100 p-1.5 rounded-full mr-2">
        <BuildingOfficeIcon className="h-4 w-4 text-red-500" />
      </div>
      <div>
        <div className="text-[10px] uppercase font-bold text-red-500">
          Target
        </div>
        <h5 className="text-sm font-semibold text-gray-700">
          {spaceInfo.spaceHeader}
        </h5>
      </div>
    </div>
    <div className="text-xs text-gray-600 mt-1.5 pl-1">
      <p>
        <span className="text-gray-500">Space Number°:</span>{" "}
        <span className="font-medium">{spaceInfo.spaceNumber}</span>
      </p>
    </div>
  </div>
);

const formatCurrency = (value?: number, currency?: string) => {
  if (value === undefined || value === null) return "N/A";
  const code = currency && currency.length === 3 ? currency.toUpperCase() : "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toFixed(0)} ${code}`;
  }
};

const formatArea = (value?: number, unit?: string) => {
  if (value === undefined || value === null) return "N/A";
  const normalized = (unit || "").toLowerCase();
  if (normalized === "m2" || normalized === "m²" || normalized === "sqm") {
    return `${value.toFixed(1)} m²`;
  }
  return `${value.toFixed(0)} ${unit || "sq ft"}`;
};

// Backwards-compatible alias for legacy call sites that pass sq-ft values
const formatSquareFeet = (value?: number, unit?: string) => formatArea(value, unit);

const CombinationCard: React.FC<CombinationCardProps> = ({
  combination,
  onHighlight,
  currentPlanName,
  currentPropertyNumber,
  onSwitchPlan
}) => {
  const getRankColor = (rank?: number) => {
    if (rank === 1) return "border-blue-500 bg-blue-50";
    if (rank === 2) return "border-indigo-400 bg-indigo-50";
    if (rank === 3) return "border-purple-600 bg-purple-50";
    return "border-gray-300 bg-gray-50";
  };

  const getRankBadgeColor = (rank?: number) => {
    if (rank === 1) return "bg-blue-500";
    if (rank === 2) return "bg-indigo-400";
    if (rank === 3) return "bg-purple-600";
    return "bg-gray-500";
  };

  // Resolve possibly-renamed fields (new payloads use simpler names without
  // unit suffixes and ship explicit currency / area_unit values)
  const totalArea = combination.total_area_sf ?? combination.total_area;
  const totalRent = combination.total_rent_usd ?? combination.total_rent;
  const comboAreaUnit = combination.area_unit;
  const comboCurrency = combination.currency;

  // Derive a display title when combination_id is missing
  const comboTitle =
    combination.combination_id ||
    (combination.spaces || [])
      .map((s) => s.space_number)
      .filter(Boolean)
      .join(" + ") ||
    "—";

  // Derive earliest expiration from spaces when not provided at combination level
  const spaceLeaseEndDates = (combination.spaces || [])
    .map((s) => s.lease_end_date)
    .filter((d): d is string => !!d);
  const derivedEarliestExpiration = spaceLeaseEndDates.length
    ? spaceLeaseEndDates.reduce((min, d) => (d < min ? d : min))
    : undefined;
  const earliestExpiration = combination.earliest_expiration || derivedEarliestExpiration;

  return (
    <div
      className={`flex-none w-96 bg-white border-l-4 ${getRankColor(
        combination.rank
      )} border-t border-r border-b border-gray-200 rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow duration-200`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center">
          <div
            className={`${getRankBadgeColor(
              combination.rank
            )} p-1.5 rounded-full mr-2`}
          >
            <Squares2X2Icon className="h-4 w-4 text-white" />
          </div>
          <div>
            <h5 className="text-sm font-semibold text-gray-800 truncate max-w-[220px]">
              Combination {comboTitle}
            </h5>
          </div>
        </div>
      </div>

      {/* Combination Metrics */}
      <div className="mb-2 pt-2 border-t border-gray-200">
        <div className="text-xs text-gray-600 space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-500">Total Area:</span>
            <span className="font-medium">
              {formatArea(totalArea, comboAreaUnit)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Total Rent:</span>
            <span className="font-medium">
              {formatCurrency(totalRent, comboCurrency)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Earliest Expiration:</span>
            <span className="font-medium">
              {earliestExpiration
                ? new Date(earliestExpiration).toLocaleDateString()
                : "N/A"}
            </span>
          </div>
          {combination.days_until_earliest_expiration !== undefined && (
            <div className="flex justify-between">
              <span className="text-gray-500">Days Until Expiration:</span>
              <span className="font-medium">
                {combination.days_until_earliest_expiration}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Spaces in Combination */}
      {combination.spaces && combination.spaces.length > 0 && (
        <div className="mb-2 pt-2 border-t border-gray-200">
          <div className="text-[10px] text-gray-500 font-semibold mb-1">
            Spaces ({combination.spaces.length})
          </div>
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {combination.spaces.map((space, idx) => {
              // Check if this space has a different plan_name or property_number
              const isDifferentPlan = space.plan_name && space.plan_name !== currentPlanName;
              const isDifferentProperty = space.property_number && space.property_number !== currentPropertyNumber;
              const showSwitchButton = (isDifferentPlan || isDifferentProperty) && onSwitchPlan;

              return (
                <div
                  key={idx}
                  className="bg-gray-50 rounded p-2 border border-gray-200"
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] font-semibold text-gray-700">
                        {space.space_number}
                      </span>

                      {/* Switch Button next to space_number */}
                      {showSwitchButton && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();

                            // Filter all spaces from this combination that are on the same plan
                            const spacesToHighlight = combination.spaces
                              .filter(s =>
                                s.plan_name === space.plan_name &&
                                s.property_number === space.property_number
                              )
                              .map(s => s.space_number);

                            onSwitchPlan(
                              space.plan_name!,
                              space.property_number,
                              spacesToHighlight
                            );
                          }}
                          className={`flex items-center p-0.5 rounded transition-colors ${isDifferentProperty
                            ? 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                            : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                            }`}
                          title={
                            isDifferentProperty
                              ? `Switch to property: ${space.property_number} / plan: ${space.plan_name}`
                              : `Switch to plan: ${space.plan_name}`
                          }
                        >
                          <ArrowsRightLeftIcon className="h-3 w-3" />
                        </button>
                      )}

                      {/* Plan and Property Badges */}
                      {showSwitchButton && (
                        <>
                          {space.plan_name && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${isDifferentProperty
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-orange-100 text-orange-700'
                              }`}>
                              {space.plan_name}
                            </span>
                          )}
                          {space.property_number && isDifferentProperty && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-purple-100 text-purple-700">
                              {space.property_number}
                            </span>
                          )}
                        </>
                      )}

                      <span className="text-[10px] text-gray-600">
                        - {space.tenant_name}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-1 text-[10px] text-gray-600">
                    <div>
                      <span className="text-gray-500">Area: </span>
                      <span className="font-medium">
                        {formatArea(
                          space.area_sf ?? space.area,
                          space.area_unit || comboAreaUnit
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Rent: </span>
                      <span className="font-medium">
                        {formatCurrency(
                          space.total_rent_usd ?? space.total_rent,
                          space.currency || comboCurrency
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Expires: </span>
                      <span className="font-medium">
                        {space.lease_end_date
                          ? new Date(space.lease_end_date).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    {space.sales_r12_usd !== null &&
                      space.sales_r12_usd !== undefined && (
                        <div>
                          <span className="text-gray-500">Sales R12: </span>
                          <span className="font-medium">
                            {formatCurrency(
                              space.sales_r12_usd,
                              space.currency || comboCurrency
                            )}
                          </span>
                        </div>
                      )}
                    {space.occupancy_percentage !== null &&
                      space.occupancy_percentage !== undefined && (
                        <div>
                          <span className="text-gray-500">Occupancy: </span>
                          <span className="font-medium">
                            {space.occupancy_percentage.toFixed(2)}%
                          </span>
                        </div>
                      )}
                    {(space.kickout_option === "Y" ||
                      space.kickout_option === "Yes") && (
                      <div className="text-orange-600 font-semibold col-span-2">
                        Kickout Available
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Strengths */}
      {combination.analysis?.strengths &&
        combination.analysis.strengths.length > 0 && (
          <div className="pt-2 border-t border-gray-200">
            <div className="text-[10px] text-green-600 font-semibold mb-1">
              ✓ Strengths
            </div>
            <div className="text-[10px] text-gray-600 line-clamp-2">
              {combination.analysis.strengths[0]}
            </div>
          </div>
        )}

      {/* Considerations */}
      {combination.analysis?.considerations &&
        combination.analysis.considerations.length > 0 && (
          <div className="pt-2 border-t border-gray-200">
            <div className="text-[10px] text-orange-600 font-semibold mb-1">
              ⚠ Considerations
            </div>
            <div className="text-[10px] text-gray-600 line-clamp-2">
              {combination.analysis.considerations[0]}
            </div>
          </div>
        )}

      {/* Highlight Spaces Button */}
      {combination.spaces && combination.spaces.length > 0 && onHighlight && (
        <div className="pt-2 border-t border-gray-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onHighlight(combination.spaces);
            }}
            className="w-full mt-2 py-1.5 px-2 bg-orange-600 text-white text-[10px] font-semibold rounded hover:bg-orange-700 transition-colors"
          >
            Highlight Spaces
          </button>
        </div>
      )}
    </div>
  );
};

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  onHighlight,
  targetSpaceNumber,
  targetHighlightColor,
}) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="flex-none w-80 bg-white border-l-4 border-l-[#0f3557] border-t border-r border-b border-gray-200 rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow duration-200">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center">
          <div className="bg-[#0f3557] p-1.5 rounded-full mr-2">
            <BuildingOfficeIcon className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-[#0f3557]">
              Recommendation
            </div>
            <h5 className="text-sm font-semibold text-gray-800 truncate max-w-[220px]">
              {recommendation.tenant_name}
            </h5>
          </div>
        </div>
      </div>

      {/* Financial Metrics */}
      {recommendation.financial_metrics && (
        <div className="mb-2 pt-2 border-t border-gray-200">
          <div className="text-[10px] text-gray-500 font-semibold mb-1.5">
            📊 Financial Metrics
          </div>
          <div className="text-xs text-gray-600 space-y-1">
            {recommendation.financial_metrics.avg_sales_usd != null && (
              <div className="flex justify-between">
                <span className="text-gray-500">Avg Sales:</span>
                <span className="font-medium">
                  {formatCurrency(
                    recommendation.financial_metrics.avg_sales_usd
                  )}
                </span>
              </div>
            )}
            {recommendation.financial_metrics.avg_rent_per_sf != null && recommendation.financial_metrics.avg_rent_per_sf !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-500">Avg Rent/SF:</span>
                <span className="font-medium">
                  ${recommendation.financial_metrics.avg_rent_per_sf.toFixed(2)}
                </span>
              </div>
            )}
            {recommendation.financial_metrics.avg_footprint_sf != null && recommendation.financial_metrics.avg_footprint_sf !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-500">Avg Footprint:</span>
                <span className="font-medium">
                  {formatSquareFeet(
                    recommendation.financial_metrics.avg_footprint_sf
                  )}
                </span>
              </div>
            )}
            {recommendation.financial_metrics.location_count != null && recommendation.financial_metrics.location_count !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-500">Locations:</span>
                <span className="font-medium">
                  {recommendation.financial_metrics.location_count}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Co-tenant Alignment */}
      {recommendation.co_tenant_alignment && (
        <div className="mb-2 pt-2 border-t border-gray-200">
          <div className="text-[10px] text-gray-500 font-semibold mb-1.5">
            🤝 Co-tenant Alignment
          </div>
          <div className="text-xs text-gray-600 space-y-1">
            {recommendation.co_tenant_alignment.overlap_count !== null && recommendation.co_tenant_alignment.overlap_count !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-500">Common Co-tenants:</span>
                <span className="font-medium">
                  {recommendation.co_tenant_alignment.overlap_count}
                </span>
              </div>
            )}
            {recommendation.co_tenant_alignment.anchor_brands_nearby &&
              recommendation.co_tenant_alignment.anchor_brands_nearby.length >
              0 && (
                <div className="mt-1">
                  <span className="text-gray-500 text-[10px] block mb-1">
                    Selected co-tenant(s):
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {recommendation.co_tenant_alignment.anchor_brands_nearby
                      .slice(0, 3)
                      .map((anchor, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 border border-yellow-300"
                        >
                          {anchor}
                        </span>
                      ))}
                    {recommendation.co_tenant_alignment.anchor_brands_nearby
                      .length > 3 && (
                        <span className="text-[10px] text-gray-500">
                          +
                          {recommendation.co_tenant_alignment.anchor_brands_nearby
                            .length - 3}{" "}
                          more
                        </span>
                      )}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Success Pattern */}
      {recommendation.success_pattern && (
        <div className="mb-2 pt-2 border-t border-gray-200">
          <div className="text-[10px] text-gray-500 font-semibold mb-1.5">
            🎯 Success Pattern
          </div>
          <div className="text-xs text-gray-600 space-y-1">
            {recommendation.success_pattern.best_performing_mall && (
              <div className="flex justify-between">
                <span className="text-gray-500">Best Location:</span>
                <span className="font-medium truncate ml-2">
                  {recommendation.success_pattern.best_performing_mall} {recommendation.success_pattern.best_performing_plan ? `(${recommendation.success_pattern.best_performing_plan})` : ''}
                </span>
              </div>
            )}
            {recommendation.success_pattern.best_sales_usd != null && recommendation.success_pattern.best_sales_usd !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-500">Best Sales:</span>
                <span className="font-medium">
                  {formatCurrency(
                    recommendation.success_pattern.best_sales_usd
                  )}
                </span>
              </div>
            )}
            {recommendation.success_pattern.co_tenants_at_best &&
              recommendation.success_pattern.co_tenants_at_best.length > 0 && (
                <div className="mt-1">
                  <span className="text-gray-500 text-[10px] block mb-1">
                    Co-tenants at Best Location:
                  </span>
                  <span className="text-gray-700 text-[10px]">
                    {recommendation.success_pattern.co_tenants_at_best
                      .slice(0, 3)
                      .join(", ")}
                    {recommendation.success_pattern.co_tenants_at_best.length >
                      3
                      ? ` +${recommendation.success_pattern.co_tenants_at_best
                        .length - 3
                      } more`
                      : ""}
                  </span>
                </div>
              )}
            {recommendation.success_pattern.willowbrook_overlap &&
              recommendation.success_pattern.willowbrook_overlap.length > 0 && (
                <div className="mt-1">
                  <span className="text-gray-500 text-[10px] block mb-1">
                    Willowbrook Overlap:
                  </span>
                  <span className="text-gray-700 text-[10px]">
                    {recommendation.success_pattern.willowbrook_overlap
                      .slice(0, 3)
                      .join(", ")}
                    {recommendation.success_pattern.willowbrook_overlap.length >
                      3
                      ? ` +${recommendation.success_pattern.willowbrook_overlap
                        .length - 3
                      } more`
                      : ""}
                  </span>
                </div>
              )}
          </div>
        </div>
      )}

      <div className="pt-2 border-t border-gray-200">
        <button
          onClick={(e) => {
            e.stopPropagation();
            const rawNeighbors = recommendation.co_tenant_alignment?.common_neighbors || [];
            const coTenants = Array.isArray(rawNeighbors)
              ? rawNeighbors
              : typeof rawNeighbors === "string"
                ? (rawNeighbors as string).split(",").map((s) => s.trim()).filter(Boolean)
                : [];
            const targetSpaceNumber = recommendation.target_space?.space_number || "";
            if (onHighlight) {
              onHighlight(recommendation.tenant_name, coTenants, targetSpaceNumber, targetHighlightColor);
            }
          }}
          className="w-full mt-2 py-1.5 px-2 bg-orange-600 text-white text-[10px] font-semibold rounded hover:bg-orange-700 transition-colors"
        >
          Highlight Result
        </button>
      </div>

      {/* View Co-tenants Button */}
      {recommendation.co_tenant_alignment &&
        recommendation.co_tenant_alignment.common_neighbors &&
        recommendation.co_tenant_alignment.common_neighbors.length > 0 && (
          <div className="pt-2 border-t border-gray-200">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(true);
              }}
              className="w-full mt-2 py-1.5 px-2 bg-[#0f3557] text-white text-[10px] font-semibold rounded hover:bg-[#0a2338] transition-colors"
            >
              View Co-tenants (
              {recommendation.co_tenant_alignment.overlap_count})
            </button>
          </div>
        )}

      {/* Co-tenants Modal */}
      {showModal && recommendation.co_tenant_alignment && (
        <CoTenantsModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          tenantName={recommendation.tenant_name}
          coTenantAlignment={recommendation.co_tenant_alignment}
        />
      )}
    </div>
  );
};

const SearchResultCard: React.FC<SearchResultCardProps> = ({
  result,
  onClick,
  currentPlanName,
  currentPropertyNumber,
  onSwitchPlan,
}) => {
  const { config } = useConfig();

  // Fonction for check if unit is in metric
  const isMetricUnit = (result: SearchResultSpaceInfo): boolean => {
    return "area_m2" in result;
  };

  const isMetric = isMetricUnit(result);

  // Function for getting area and distance units
  const getAreaUnit = () => (isMetric ? "m²" : "sq ft");
  const getDistanceUnit = () => (isMetric ? "m" : "ft");

  // Get mall name from result if available
  const mallName = result.mall_name || result.property_name;

  // Check if this result is from a different plan
  const isDifferentPlan = result.plan_name && result.plan_name !== currentPlanName;

  // Check if this result is from a different property
  const isDifferentProperty = result.property_number && result.property_number !== currentPropertyNumber;

  const handleSwitchPlan = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (result.plan_name && onSwitchPlan) {
      onSwitchPlan(result.plan_name, result.property_number);
    }
  };

  return (
    <div
      className="flex-none w-64 bg-white border-l-4 border-l-green-500 border-t border-r border-b border-gray-200 rounded-lg shadow-md p-3 cursor-pointer hover:shadow-lg transition-shadow duration-200"
      onClick={() => onClick(result)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="bg-green-100 p-1.5 rounded-full mr-2">
            <BuildingOfficeIcon className="h-4 w-4 text-green-500" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-green-500">
              {mallName || "Result"}
            </div>
            <h5 className="text-sm font-semibold text-gray-700">
              {result.tenant}
            </h5>
          </div>
        </div>
        {/* Switch Plan/Property Button - shows when plan or property differs */}
        {(isDifferentPlan || isDifferentProperty) && onSwitchPlan && (
          <button
            onClick={handleSwitchPlan}
            className={`flex items-center gap-1 p-1.5 rounded-md transition-colors ${isDifferentProperty
              ? 'bg-purple-100 text-purple-600 hover:bg-purple-200 hover:text-purple-700'
              : 'bg-orange-100 text-orange-600 hover:bg-orange-200 hover:text-orange-700'
              }`}
            title={
              isDifferentProperty
                ? `Switch to property: ${result.property_number} / plan: ${result.plan_name}`
                : `Switch to plan: ${result.plan_name}`
            }
            aria-label={
              isDifferentProperty
                ? `Switch to property ${result.property_number} and plan ${result.plan_name}`
                : `Switch to plan ${result.plan_name}`
            }
          >
            <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="text-xs text-gray-600 mt-1.5 pl-1">
        <div className="grid grid-cols-2 gap-x-1">
          {result.spaceID !== undefined && result.spaceID !== "" && (
            <>
              <span className="text-gray-500">Space #:</span>
              <span className="font-medium truncate">{result.spaceID}</span>
            </>
          )}

          {result.area !== undefined &&
            result.area !== null &&
            result.area > 0 && (
              <>
                <span className="text-gray-500">Area:</span>
                <span className="font-medium">
                  {result.area} {getAreaUnit()}
                </span>
              </>
            )}
          {result.distance !== undefined &&
            result.distance !== null &&
            result.distance > 0 && (
              <>
                <span className="text-gray-500">Distance:</span>
                <span className="font-medium">
                  {result.distance} {getDistanceUnit()}
                </span>
              </>
            )}
          {config.chatResultDisplayMetrics === true &&
            result.totalRentPSF !== undefined &&
            result.totalRentPSF !== "" && (
              <>
                <span className="text-gray-500">
                  Total Rent pr {getAreaUnit()}:
                </span>
                <span className="font-medium">{result.totalRentPSF}</span>
              </>
            )}
          {config.chatResultDisplayMetrics === false &&
            result.totalRentPSF !== undefined &&
            result.totalRentPSF !== "" && (
              <>
                <span className="text-gray-500">
                  Total Rent pr {getAreaUnit()}:
                </span>
                <span className="font-medium">{result.totalRentPSF}</span>
              </>
            )}
          {result.leaseEndDate !== undefined && result.leaseEndDate !== "" && (
            <>
              <span className="text-gray-500">Lease End Date:</span>
              <span className="font-medium">
                {result.leaseEndDate.split("T")[0]}
              </span>
            </>
          )}
          {result.totalRent !== undefined && result.totalRent !== "" && (
            <>
              <span className="text-gray-500">Total Rent:</span>
              <span className="font-medium">
                {formatCurrency(Number(result.totalRent))}
              </span>
            </>
          )}
          {/*   10.81.6
       // sales and sales PSF    */}

          {config.chatResultDisplayMetrics === true &&
            result.totalSalesPSF !== undefined &&
            result.totalSalesPSF !== "" && (
              <>
                <span className="text-gray-500">
                  Total Sales pr {getAreaUnit()}:
                </span>
                <span className="font-medium">
                  {formatCurrency(Number(result.totalSalesPSF))}
                </span>
              </>
            )}
          {result.totalSales !== undefined && result.totalSales !== "" && (
            <>
              <span className="text-gray-500">Total Sales:</span>
              <span className="font-medium">
                {formatCurrency(Number(result.totalSales))}
              </span>
            </>
          )}
          {/* Plan Name - show only if different from current and not N/A */}
          {result.plan_name && result.plan_name !== 'N/A' && (
            <>
              <span className="text-gray-500">Plan:</span>
              <span className={`font-medium ${isDifferentProperty ? 'text-purple-600' : isDifferentPlan ? 'text-orange-600' : ''}`}>
                {result.plan_name}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface ChatMessageDisplayProps {
  message: ChatMessage | ChatMessageV2 | ChatMessageV5;
  currentHighlight: string | null;
  internalCADViewerAICallback: (
    chatMessage: ChatMessage | ChatMessageV2 | ChatMessageV5
  ) => void;
  onSetInputValue: (value: string) => void;
  onRateMessage?: (message: ChatMessage | ChatMessageV2 | ChatMessageV5) => void;
  /** Start a follow-up on this response (shows it as a reply quote + focus). */
  onFollowUp?: (replyContent: string) => void;
}

const ChatMessageDisplay: React.FC<ChatMessageDisplayProps> = ({
  message,
  currentHighlight,
  internalCADViewerAICallback,
  onSetInputValue,
  onRateMessage,
  onFollowUp,
}) => {
  const [isRawVisible, setIsRawVisible] = useState(false);
  const [isTraceModalOpen, setIsTraceModalOpen] = useState(false);
  const [isExportingPlanPdf, setIsExportingPlanPdf] = useState(false);
  const [isResultsVisible, setIsResultsVisible] = useState(true);
  const [isMetadataVisible, setIsMetadataVisible] = useState(true);
  const [isTableView, setIsTableView] = useState(false); // Toggle between cards and table view
  const [previousSpaceIDClicked, setPreviousSpaceIDClicked] = useState<
    string | undefined
  >(undefined);
  const { config } = useConfig();

  // Ref to store pending space highlights after plan switch (with optional custom color)
  const pendingHighlightSpaces = useRef<{ spaceNum: string; highlight_color?: string }[]>([]);

  const {
    setSelectedSpaceObjectID,
    plansInfo,
    selectedPlan,
    selectedPlanDetail,
    setSelectedPlan,
    setSelectedPlanDetail,
    setCurrentDrawingPath,
    setMultiSelectArray,
    signalDrawingLoadStart,
    drawingLoaded,
    drawingLoadGeneration,
    currentCanvasInstance,
  } = useContext(CadviewerContext);

  // Zustand store for space selection
  const setSpaceSelectedForAI = useSpaceSelectionStore(state => state.setSpaceSelectedForAI);

  // Get search results for highlighting after plan switch
  const searchResults = (message as any)?.result?.searchResults as SearchResultSpaceInfo[] | undefined;

  // Get table data for highlighting after plan switch (when tables are received directly)
  const tableResults = (message as any)?.result?.tables?.data as Record<string, any>[] | undefined;

  // Function to highlight spaces for recommendations
  const handleHighlightRecommendation = (
    tenantName: string,
    coTenants: string[],
    targetSpaceNumber?: string,
    targetHighlightColor?: string
  ) => {
    if (!plansInfo?.spaces) return;

    // Clear existing highlights
    cadviewer.cvjs_clearSpaceLayer();

    // First, highlight the target space in green or custom color (if provided)
    if (targetSpaceNumber) {
      const normalizedTargetSpace = targetSpaceNumber
        .toString()
        .replaceAll(".", "_")
        .replaceAll(" ", "_");
      console.log("[handleHighlightRecommendation] Highlighting target space in green:", normalizedTargetSpace);
      const targetStyle = targetHighlightColor
        ? createHighlightStyle(targetHighlightColor)
        : highlight_green;
      cadviewer.cvjs_highlightSpace(normalizedTargetSpace, targetStyle);
    }

    // Normalize tenant names for comparison (case-insensitive)
    const normalizedTenantName = tenantName.toLowerCase().trim();
    const normalizedCoTenants = coTenants.map((t) => t.toLowerCase().trim());

    // Find and highlight all spaces for the recommended tenant (red)
    plansInfo.spaces.forEach((space: SpaceInfo) => {
      const spaceHeader = space.spaceHeader?.toLowerCase().trim() || "";
      const spaceNumber =
        space.spaceNumber?.replaceAll(".", "_").replaceAll(" ", "_") || "";

      if (spaceHeader === normalizedTenantName && spaceNumber) {
        cadviewer.cvjs_highlightSpace(spaceNumber, highlight_red);
      }
    });

    // Find and highlight all spaces for co-tenants (yellow)
    plansInfo.spaces.forEach((space: SpaceInfo) => {
      const spaceHeader = space.spaceHeader?.toLowerCase().trim() || "";
      const spaceNumber =
        space.spaceNumber?.replaceAll(".", "_").replaceAll(" ", "_") || "";

      if (
        normalizedCoTenants.includes(spaceHeader) &&
        spaceNumber &&
        spaceHeader !== normalizedTenantName
      ) {
        cadviewer.cvjs_highlightSpace(spaceNumber, highlight_yellow);
      }
    });
  };

  // Function to highlight a single relocation space (focus on click)
  const handleHighlightRelocationSpace = (
    spaceNumber: string,
    isVacant: boolean
  ) => {
    if (!spaceNumber) return;
    cadviewer.cvjs_clearSpaceLayer();
    const normalized = spaceNumber
      .replaceAll(".", "_")
      .replaceAll(" ", "_");
    const targetSpace = message.result?.relocation?.target_space;
    const isTarget = !!targetSpace && String(targetSpace) === spaceNumber;
    const style = isTarget
      ? highlight_red
      : isVacant
        ? highlight_yellow
        : highlight_green;
    cadviewer.cvjs_highlightSpace(normalized, style);
    cadviewer.cvjs_hideOnlyPop();
    cadviewer.cvjs_zoomHere_ObjectId(normalized, config.ZoomFactor);
  };

  // Function to highlight all allowed relocation spaces
  const handleHighlightRelocationAll = (
    spaceNumbers: string[],
    detailsByNumber: Map<string, RelocationSpaceDetail>
  ) => {
    cadviewer.cvjs_clearSpaceLayer();
    const targetSpace = message.result?.relocation?.target_space
      ? String(message.result.relocation.target_space)
      : "";
    spaceNumbers.forEach((raw) => {
      if (!raw) return;
      const rawStr = String(raw);
      if (targetSpace && rawStr === targetSpace) return;
      const normalized = rawStr.replaceAll(".", "_").replaceAll(" ", "_");
      const detail = detailsByNumber.get(rawStr);
      const style = detail?.highlight_color
        ? createHighlightStyle(detail.highlight_color)
        : isVacantTenant(detail?.tenant_name)
          ? highlight_yellow
          : highlight_green;
      cadviewer.cvjs_highlightSpace(normalized, style);
    });
    if (targetSpace) {
      const normalizedTarget = targetSpace
        .replaceAll(".", "_")
        .replaceAll(" ", "_");
      cadviewer.cvjs_highlightSpace(normalizedTarget, highlight_red);
    }
  };

  // Function to highlight spaces for combinations
  const handleHighlightCombination = (spaces: SpaceCombinationSpace[]) => {
    // Clear existing highlights
    cadviewer.cvjs_clearSpaceLayer();

    // Highlight each space in the combination (use highlight_color if provided)
    spaces.forEach((space) => {
      const spaceNumber = space.space_number
        ?.replaceAll(".", "_")
        .replaceAll(" ", "_");

      if (spaceNumber) {
        const style = space.highlight_color
          ? createHighlightStyle(space.highlight_color)
          : highlight_yellow;
        cadviewer.cvjs_highlightSpace(spaceNumber, style);
      }
    });

    // Focus on the first space in the combination
    if (spaces.length > 0 && spaces[0].space_number) {
      // Hide any open popups
      cadviewer.cvjs_hideOnlyPop();

      cadviewer.cvjs_zoomHere_ObjectId(
        spaces[0].space_number,
        config.ZoomFactor
      );
    }
  };

  // Function to highlight spaces from table row click
  const handleTableRowClick = (rowData: Record<string, any>) => {
    // Find the space_number field in the row data
    const spaceNumberField = Object.keys(rowData).find((field) =>
      isSpaceNumberField(field)
    );

    if (!spaceNumberField) return;

    const spaceNumberValue = rowData[spaceNumberField];
    if (!spaceNumberValue) return;

    // Handle both single space number and comma-separated list
    const spaceNumbers = String(spaceNumberValue)
      .split(",")
      .map((s) => s.trim());

    // Clear existing highlights
    cadviewer.cvjs_clearSpaceLayer();

    // Highlight each space
    spaceNumbers.forEach((spaceNum) => {
      // Normalize space number (replace dots and spaces with underscores)
      const normalizedSpaceNum = spaceNum
        .replaceAll(".", "_")
        .replaceAll(" ", "_");

      // Highlight the space with custom color if provided, otherwise yellow
      const style = rowData.highlight_color
        ? createHighlightStyle(rowData.highlight_color)
        : highlight_yellow;
      cadviewer.cvjs_highlightSpace(normalizedSpaceNum, style);

      // Optionally show the space info panel
      if (config.ShowInformativeBottomPanelWhenClickInLeftPanel) {
        setSelectedSpaceObjectID(normalizedSpaceNum);
      }
    });
  };

  // Wing Analysis: re-highlight the whole wing and export the highlighted plan.
  const wingSpaceNumbers: string[] =
    (message as any)?.result?.wingAnalysis?.spaceNumbers || [];

  const handleHighlightWing = () => {
    cadviewer.cvjs_clearSpaceLayer();
    wingSpaceNumbers.forEach((raw) => {
      const normalized = String(raw).replaceAll(".", "_").replaceAll(" ", "_");
      cadviewer.cvjs_highlightSpace(normalized, highlight_blue);
    });
    cadviewer.cvjs_hideOnlyPop();
  };

  const handleExportWingPlan = async () => {
    const wingTable = (message as any)?.result?.tables;
    if (!wingTable) return;

    // Ensure the wing is highlighted before the plan is published/captured.
    handleHighlightWing();

    // Zoom to the full drawing extents so the published Quick PDF captures the
    // whole plan (otherwise it reflects the current zoom/pan and gets cropped).
    try {
      const cvZoom = cadviewer as any;
      if (typeof cvZoom.cvjs_zoomExtents === "function") cvZoom.cvjs_zoomExtents();
      else if (typeof cvZoom.cvjs_resetZoomPan === "function")
        cvZoom.cvjs_resetZoomPan();
    } catch (_) {}

    const spaces = (wingTable.data || [])
      .filter((r: any) => !r._is_summary)
      .map((r: any) => ({
        number: String(r.space_number ?? ""),
        tenant: String(r.tenant_name ?? ""),
      }));

    // Brand logos for the header banner: ours (CADViewer) + the customer's
    // (the configured top-bar logo). Missing/failed logos are simply omitted.
    const wingPdf = await import("../../utils/wingPdfExport");
    const logos = await wingPdf.loadReportLogos({
      ourLogoUrl: "/CADViewer_Primary_logo.svg",
      customerLogoUrl: config.topBarLogo || undefined,
    });

    const pdfParams = {
      propertyName: selectedPlan?.propertyName,
      propertyNumber: selectedPlan?.propertyNumber,
      planName: selectedPlanDetail?.planName,
      spaces,
      tableData: wingTable,
      logos,
      showGeneratedDate: config.wingPdfConfig?.showGeneratedDate ?? true,
      includeCoverPage: config.wingPdfConfig?.includeCoverPage ?? true,
      generatedDateFormat:
        config.generatedDateFormat ?? "YYYY-MM-DD HH:mm:ss",
      generatedPdfDateFormat:
        config.generatedDateFormat ??
        "YYYY-MM-DD HH:mm:ss",
      use12HourTime: config.use12HourTime ?? false,
    };

    // Trigger the native CADViewer Quick PDF for the plan page(s).
    const canvas = currentCanvasInstance || "floorPlan";
    const cv = cadviewer as any;
    const publish = (fileName: string) => {
      try {
        cv.cvjs_setPrintOrientation?.("landscape");
      } catch (_) {}
      try {
        cv.cvjs_setPrintPaperSize?.(config.quickPDF_1_papersize || "letter");
      } catch (_) {}
      try {
        cv.cvjs_setPrintResolutionDpi?.(
          config.quickPDF_default_resolution || 300
        );
      } catch (_) {}
      try {
        cv.cvjs_setPublishPDFfilename?.(canvas, fileName);
      } catch (_) {}
      cv.cvjs_publishPDF(canvas);
    };

    setIsExportingPlanPdf(true);
    try {
      // Give the highlight + zoom-to-extents time to repaint before publishing.
      await new Promise((resolve) => setTimeout(resolve, 400));
      // Preferred: merge the native Quick PDF (high quality) with cover + table.
      const { exportWingPlanPdfNative } = await import(
        "../../utils/wingNativePdfExport"
      );
      await exportWingPlanPdfNative(pdfParams, publish);
    } catch (e) {
      console.warn(
        "Native Quick PDF merge failed; falling back to composed PDF:",
        e
      );
      // Fallback: fully client-side composed PDF (html2canvas plan capture).
      try {
        await wingPdf.exportWingPlanPdf(pdfParams);
      } catch (e2) {
        console.error("Wing PDF export failed:", e2);
      }
    } finally {
      setIsExportingPlanPdf(false);
    }
  };

  // Function to switch to a different plan or property
  // Supports both same-property plan switching and cross-property switching
  const handleSwitchPlan = async (planName: string, propertyNumber?: string) => {
    console.log(`[DeepLink] handleSwitchPlan() — planName="${planName}", propertyNumber="${propertyNumber}", currentPlan="${selectedPlanDetail?.planName}", currentProperty="${selectedPlan?.propertyNumber}"`);
    // Determine if we need to switch to a different property
    const isCrossPropertySwitch = propertyNumber && propertyNumber !== selectedPlan?.propertyNumber;

    // Collect all space IDs from searchResults or tableResults that match the target
    const spacesToHighlight: { spaceNum: string; highlight_color?: string }[] = [];

    // From searchResults (exact_matches)
    if (searchResults) {
      searchResults.forEach((result) => {
        const matchesPlan = result.plan_name === planName;
        const matchesProperty = !propertyNumber || result.property_number === propertyNumber;
        if (matchesPlan && matchesProperty && result.spaceID) {
          const normalizedSpaceNum = result.spaceID
            .replaceAll(".", "_")
            .replaceAll(" ", "_");
          spacesToHighlight.push({
            spaceNum: normalizedSpaceNum,
            highlight_color: result.highlight_color,
          });
        }
      });
    }

    // From tableResults (direct tables) - look for space_number or spaceID field
    if (tableResults && spacesToHighlight.length === 0) {
      tableResults.forEach((row) => {
        const spaceNum = row.space_number || row.spaceID || row.space_id;
        const rowPlanName = row.plan_name;
        const rowPropertyNumber = row.property_number;
        const matchesPlan = rowPlanName === planName;
        const matchesProperty = !propertyNumber || rowPropertyNumber === propertyNumber;
        if (matchesPlan && matchesProperty && spaceNum) {
          const normalizedSpaceNum = String(spaceNum)
            .replaceAll(".", "_")
            .replaceAll(" ", "_");
          spacesToHighlight.push({
            spaceNum: normalizedSpaceNum,
            highlight_color: row.highlight_color,
          });
        }
      });
    }

    // Store pending highlights
    pendingHighlightSpaces.current = spacesToHighlight;
    console.log(`[DeepLink] handleSwitchPlan() — ${spacesToHighlight.length} spaces to highlight, isCrossProperty=${!!isCrossPropertySwitch}`, spacesToHighlight.map(s => s.spaceNum));

    if (isCrossPropertySwitch) {
      // Cross-property switch: need to fetch plans data to find the target property
      try {
        const {
          globalApplicationFilesLocation,
          globalApplicationPlans,
          ServerBackEndUrl,
          postFixServerToken,
          propertySVGFilesLocation
        } = config;

        const url = globalApplicationFilesLocation + "" + globalApplicationPlans;
        let endpoint = "loadjsonpostfix/proxy";
        if (ServerBackEndUrl.substring(ServerBackEndUrl.length - 1) !== "/") {
          endpoint = "/" + endpoint;
        }

        let headers: Record<string, string> | undefined = undefined;
        if (postFixServerToken) {
          headers = { "Content-Type": "application/json" };
        }
        if ((config as any).bearerToken) {
          headers = { ...(headers ?? {}), Authorization: `${(config as any).bearerToken}` };
        }

        const response = await fetch(
          postFixServerToken ? ServerBackEndUrl + endpoint : url,
          {
            method: postFixServerToken ? "POST" : "GET",
            headers,
            body: postFixServerToken ? JSON.stringify({ url }) : undefined,
            redirect: "follow",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch plans data");
        }

        const data = await response.json();
        const allPlans = data.data || [];

        // Find the target property
        let targetProperty: any = null;
        let targetPlanDetail: any = null;

        for (const planType of allPlans) {
          const property = planType.properties?.find(
            (p: any) => p.propertyNumber === propertyNumber
          );
          if (property) {
            targetProperty = property;
            targetPlanDetail = property.plans?.find(
              (p: any) => p.planName === planName
            );
            // If exact plan not found, use first plan
            if (!targetPlanDetail && property.plans?.length > 0) {
              targetPlanDetail = property.plans[0];
            }
            break;
          }
        }

        if (!targetProperty || !targetPlanDetail) {
          console.warn(`Property "${propertyNumber}" or plan "${planName}" not found`);
          return;
        }

        // Load the new property and plan
        const svgFileLink = (propertySVGFilesLocation + targetPlanDetail.svgFileName)
          .replace("/v2/svg/retail/", "/svg/");

        console.log(`[DeepLink] handleSwitchPlan() CROSS-PROPERTY — ClearDrawing + LoadDrawing("${svgFileLink}")`);
        cadviewer.cvjs_hideOnlyPop();
        cadviewer.cvjs_ClearDrawing("floorPlan");
        cadviewer.cvjs_setZoomImageWallpaper(false);
        signalDrawingLoadStart();
        cadviewer.cvjs_LoadDrawing("floorPlan", svgFileLink);

        setCurrentDrawingPath(svgFileLink);
        setSelectedPlan(targetProperty);
        setSelectedPlanDetail(targetPlanDetail);
        setSelectedSpaceObjectID(undefined);
        setMultiSelectArray([]);

        console.log(`[DeepLink] handleSwitchPlan() CROSS-PROPERTY done — property=${propertyNumber}, plan=${targetPlanDetail.planName}`);
      } catch (error) {
        console.error("Error switching to different property:", error);
      }
    } else {
      // Same-property switch: use existing logic
      if (!selectedPlan?.plans) return;

      const targetPlan = selectedPlan.plans.find(
        (plan) => plan.planName === planName
      );
      if (!targetPlan) {
        console.warn(`Plan "${planName}" not found in current property`);
        return;
      }

      const { propertySVGFilesLocation } = config;
      const svgFileLink = (
        propertySVGFilesLocation + targetPlan.svgFileName
      ).replace("/v2/svg/retail/", "/svg/");

      console.log(`[DeepLink] handleSwitchPlan() SAME-PROPERTY — ClearDrawing + LoadDrawing("${svgFileLink}")`);
      cadviewer.cvjs_hideOnlyPop();
      cadviewer.cvjs_ClearDrawing("floorPlan");
      cadviewer.cvjs_setZoomImageWallpaper(false);
      signalDrawingLoadStart();
      cadviewer.cvjs_LoadDrawing("floorPlan", svgFileLink);

      setCurrentDrawingPath(svgFileLink);
      setSelectedPlanDetail(targetPlan);
      setSelectedSpaceObjectID(undefined);

      console.log(`[DeepLink] handleSwitchPlan() SAME-PROPERTY done — plan="${planName}"`);
    }
  };

  // Apply pending highlights after a drawing finishes loading.
  // Uses drawingLoadGeneration (monotonic counter) as a dependency so that React's
  // cleanup function automatically cancels stale timeouts when a new load cycle starts,
  // even if drawingLoaded stays true due to React 18 batching.
  useEffect(() => {
    console.log(`[DeepLink] Highlight effect — drawingLoaded=${drawingLoaded}, generation=${drawingLoadGeneration}, pendingSpaces=${pendingHighlightSpaces.current.length}`);

    if (!drawingLoaded || pendingHighlightSpaces.current.length === 0) {
      console.log(`[DeepLink] Highlight effect SKIP — drawingLoaded=${drawingLoaded}, pendingSpaces=${pendingHighlightSpaces.current.length}`);
      return;
    }

    console.log(`[DeepLink] Highlight effect — starting 1s timer for ${pendingHighlightSpaces.current.length} spaces (generation=${drawingLoadGeneration})`);
    const timer = setTimeout(() => {
      if (pendingHighlightSpaces.current.length > 0) {
        console.log(`[DeepLink] Highlight timer FIRED (generation=${drawingLoadGeneration}) — highlighting ${pendingHighlightSpaces.current.length} spaces:`, pendingHighlightSpaces.current.map(s => s.spaceNum));
        cadviewer.cvjs_clearSpaceLayer();
        pendingHighlightSpaces.current.forEach((item) => {
          const style = item.highlight_color
            ? createHighlightStyle(item.highlight_color)
            : highlight_green;
          cadviewer.cvjs_highlightSpace(item.spaceNum, style);
        });
        pendingHighlightSpaces.current = [];
      } else {
        console.log(`[DeepLink] Highlight timer FIRED but pendingSpaces is empty — skipping`);
      }
    }, 1000);

    return () => {
      console.log(`[DeepLink] Highlight effect CLEANUP — cancelling timer (generation=${drawingLoadGeneration})`);
      clearTimeout(timer);
    };
  }, [drawingLoaded, drawingLoadGeneration]);

  // Track if table highlights are currently active (for toggle behavior)
  const [isTableHighlightActive, setIsTableHighlightActive] = useState(false);

  // Reset table highlight state when plan or property changes (highlights are cleared on new plan load)
  useEffect(() => {
    setIsTableHighlightActive(false);
  }, [selectedPlanDetail?.planName, selectedPlan?.propertyNumber]);

  // Highlight target space in green when recommendations are displayed and we're on the matching plan
  useEffect(() => {
    // Extract target_space from recommendation response
    const targetSpace = (message as any)?.result?.target_space;
    const queryInterpretation = (message as any)?.result?.query_interpretation;
    const hasRecommendations = (message as any)?.result?.recommendations?.length > 0;

    // Only proceed if this is a recommendation response with target_space
    if (!hasRecommendations || !targetSpace) return;

    // Get the target plan and property from either target_space or query_interpretation
    const targetPlanName = targetSpace.plan_name || queryInterpretation?.plan_name;
    const targetPropertyNumber = targetSpace.property_number || queryInterpretation?.property_number;
    const targetSpaceNumber = targetSpace.space_number;

    // Check if we are on the matching plan
    const isMatchingPlan = targetPlanName === selectedPlanDetail?.planName;
    const isMatchingProperty = targetPropertyNumber === selectedPlan?.propertyNumber;

    if (isMatchingPlan && isMatchingProperty && targetSpaceNumber) {
      // Normalize space number (replace dots and spaces with underscores)
      const normalizedSpaceNum = targetSpaceNumber
        .toString()
        .replaceAll(".", "_")
        .replaceAll(" ", "_");

      console.log("[ChatMessageDisplay] Highlighting target space in green:", normalizedSpaceNum);

      // Clear existing highlights and highlight the target space (custom color or green)
      cadviewer.cvjs_clearSpaceLayer();
      const targetStyle = targetSpace.highlight_color
        ? createHighlightStyle(targetSpace.highlight_color)
        : highlight_green;
      cadviewer.cvjs_highlightSpace(normalizedSpaceNum, targetStyle);

      // Optionally zoom to the target space
      cadviewer.cvjs_hideOnlyPop();
      cadviewer.cvjs_zoomHere_ObjectId(normalizedSpaceNum, config.ZoomFactor);
    }
  }, [message, selectedPlanDetail?.planName, selectedPlan?.propertyNumber, config.ZoomFactor]);

  // Function to highlight all spaces from the current data (for global Highlight All button)
  const handleHighlightAll = useCallback(() => {
    // Check if this is a table response - internalCADViewerAICallback returns early for tables
    // so we need to handle table highlighting separately
    const hasTableData = message.result?.tables || tableResults;

    // If we have a full result object WITHOUT tables, use the internal callback which handles toggling and store state
    if (message.result && !hasTableData) {
      internalCADViewerAICallback(message);
      return;
    }

    // For table data, handle highlighting and toggle manually
    // If highlights are currently active, clear them and toggle off
    if (isTableHighlightActive) {
      cadviewer.cvjs_clearSpaceLayer();
      setIsTableHighlightActive(false);
      console.log("Table highlights cleared");
      return;
    }

    // Build list of spaces to highlight from table data
    const spacesToHighlight: { spaceNum: string; color: any }[] = [];

    // Helper to add space numbers (handles comma-separated values)
    const addSpaceNumbers = (spaceValue: any, color: any) => {
      if (!spaceValue) return;
      const spaceStr = String(spaceValue);
      // Split by comma and trim each value
      const spaces = spaceStr.split(",").map(s => s.trim()).filter(s => s);
      spaces.forEach(space => {
        const normalizedSpaceNum = space
          .replaceAll(".", "_")
          .replaceAll(" ", "_");

        // Avoid duplicates if already added with same color (or update color if priority needed? keeping simple for now)
        if (normalizedSpaceNum && !spacesToHighlight.some(item => item.spaceNum === normalizedSpaceNum)) {
          spacesToHighlight.push({ spaceNum: normalizedSpaceNum, color });
        }
      });
    };

    // Determine which table data to use
    const tableDataToProcess = tableResults || message.result?.tables?.data;

    // From tableResults (direct tables) -> GREEN
    if (tableDataToProcess) {
      tableDataToProcess.forEach((row: Record<string, any>) => {
        // Check plan and property match
        const rowPlanName = row.plan_name;
        const rowPropertyNumber = row.property_number;
        const planMatch = !rowPlanName || rowPlanName === selectedPlanDetail?.planName;
        const propertyMatch = !rowPropertyNumber || rowPropertyNumber === selectedPlan?.propertyNumber;

        if (planMatch && propertyMatch) {
          // Use custom highlight_color from the row if provided, otherwise green
          const rowColor = row.highlight_color
            ? createHighlightStyle(row.highlight_color)
            : highlight_green;
          // Find all space number fields in the row
          Object.keys(row).forEach((fieldName) => {
            if (isSpaceNumberField(fieldName)) {
              addSpaceNumbers(row[fieldName], rowColor);
            }
          });
        }
      });
    }

    if (spacesToHighlight.length > 0) {
      cadviewer.cvjs_clearSpaceLayer();
      spacesToHighlight.forEach((item) => {
        cadviewer.cvjs_highlightSpace(item.spaceNum, item.color);
      });
      setIsTableHighlightActive(true);
      console.log(`Highlighted ${spacesToHighlight.length} spaces from table data`);
    } else {
      console.log("No matching spaces found to highlight for current plan/property");
    }
  }, [searchResults, tableResults, selectedPlanDetail?.planName, selectedPlan?.propertyNumber, message.result, internalCADViewerAICallback, message, isTableHighlightActive]);

  const canShowRaw = message.role === "assistant" && message.rawContent;
  const hasSearchResults = message.role === "assistant" && message.result;

  const isUserMessage = message.role === "user";

  // Persisted reasoning (snowflake-agent provider) available to replay in modal
  const thinkingMessage = message as ChatMessageV5;
  const hasReasoning =
    message.role === "assistant" &&
    (!!thinkingMessage.thinkingText ||
      (thinkingMessage.thinkingStatuses?.length ?? 0) > 0 ||
      (thinkingMessage.reasoningTrace?.length ?? 0) > 0);

  let formattedRawContent = "";
  if (canShowRaw && message.rawContent) {
    try {
      // Attempt to parse and prettify if it's JSON
      const parsed = JSON.parse(message.rawContent);
      formattedRawContent = JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If parsing fails, show the raw string directly
      formattedRawContent = message.rawContent;
    }
  }

  const handleRetry = () => {
    // Check if the message has a space context prefix
    // Format: "For space [spaceNum] in plan [planName] of property [propertyName]: [message]"
    const spaceContextRegex = /^For space (.+?) in plan (.+?) of property (.+?): (.*)$/;
    const match = message.content.match(spaceContextRegex);

    if (match) {
      const [, spaceNumber, planName, propertyName, originalMessage] = match;

      // Restore the space selection in the store (this will show the badge)
      setSpaceSelectedForAI({
        spaceNumber,
        planName,
        propertyName,
      });

      // Set the input value to the original message without prefix
      onSetInputValue(originalMessage);
    } else {
      // Standard retry
      onSetInputValue(message.content);
    }
  };

  return (
    <div className="my-3">
      {" "}
      {/* Outer container for message + results */}
      <div
        className={`flex ${message.role === "user" ? "justify-end" : "justify-start"
          }`}
      >
        <div
          className={`relative max-w-[85%] px-4 py-2 rounded-lg shadow-sm ${message.role === "user"
            ? "bg-orange-500 text-white"
            : message.result && message.result.targetPlanInfo
              ? "bg-primary-100 text-primary-800" // Style for AI message with results
              : "bg-gray-200 text-gray-800" // Standard AI message
            }`}
        >
          {/* Main content */}
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>

          {/* Metadata display (only for user messages, hidden by default) */}
          {message.role === "user" && message.metadata && isMetadataVisible && (
            <div className="mt-2 pt-2 border-t border-white/30">
              <div className="flex flex-wrap gap-2 text-xs">
                {/* Search Mode Badge */}
                <span className={`inline-flex items-center px-2 py-0.5 rounded font-medium ${message.metadata.searchMode === "global"
                  ? "bg-green-500 text-white"
                  : message.metadata.searchMode === "property"
                    ? "bg-blue-500 text-white"
                    : "bg-white/90 text-orange-700"
                  }`}>
                  {message.metadata.searchMode === "global"
                    ? "All Malls"
                    : message.metadata.searchMode === "property"
                      ? "Within Current Property"
                      : "Current Plan"}
                </span>

                {/* Plan Info (only in current mode) */}
                {message.metadata.planInfo && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-white/90 text-blue-700 font-medium">
                    {message.metadata.planInfo.propertyName} - {message.metadata.planInfo.planName}
                  </span>
                )}

                {/* Selected Spaces Count (only count, no list) */}
                {message.metadata.selectedSpaces && message.metadata.selectedSpaces.length > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-white/90 text-indigo-700 font-medium">
                    {message.metadata.selectedSpaces.length} space{message.metadata.selectedSpaces.length > 1 ? 's' : ''} selected
                  </span>
                )}

                {/* Timestamp - Relative with absolute tooltip - WHITE for visibility */}
                {message.metadata.timestamp && (
                  <span
                    className="text-white font-medium"
                    title={new Date(message.metadata.timestamp).toLocaleString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  >
                    {getRelativeTime(message.metadata.timestamp)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Menu for actions */}
          {(canShowRaw || isUserMessage) && (
            <div className="absolute top-0 right-0 flex mt-[-15px] mr-[-5px] space-x-1">
              {/* Old Highlight Button Removed */}

              {/* Metadata toggle button for user messages (if metadata exists) */}
              {isUserMessage && message.metadata && (
                <button
                  onClick={() => setIsMetadataVisible(!isMetadataVisible)}
                  className={`${isMetadataVisible ? "bg-blue-500" : "bg-gray-500"
                    } text-white text-[10px] px-1.5 py-0.5 rounded hover:bg-gray-600 focus:outline-none flex items-center gap-0.5`}
                  aria-label="Show/hide metadata"
                  title="Show/hide query metadata"
                >
                  <Cog6ToothIcon className="w-3 h-3" />
                </button>
              )}

              {/* Retry button for user messages */}
              {isUserMessage && (
                <button
                  onClick={handleRetry}
                  className="bg-gray-500 text-white text-[10px] px-1.5 py-0.5 rounded hover:bg-gray-600 focus:outline-none"
                  aria-label="Retry this message"
                  title="Retry this message"
                >
                  Retry
                </button>
              )}

              {/* Affichage conditionnel du menu contextuel selon la config */}
              {config.enableChatMessageMenu && canShowRaw && (
                <Menu as="div" className="relative inline-block text-left">
                  <MenuButton className="bg-gray-500 text-white rounded p-0.5 hover:bg-gray-600 focus:outline-none">
                    <EllipsisHorizontalIcon
                      className="h-4 w-4"
                      aria-hidden="true"
                    />
                  </MenuButton>
                  <MenuItems className="absolute right-0 mt-1 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                    <div className="py-1">
                      {config.enableMenuShowRawJson && (
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              onClick={() => setIsRawVisible(!isRawVisible)}
                              className={`${focus ? "bg-gray-100" : ""
                                } w-full text-left block px-4 py-2 text-sm text-gray-700`}
                            >
                              {isRawVisible ? "Hide raw JSON" : "Show raw JSON"}
                            </button>
                          )}
                        </MenuItem>
                      )}
                      {config.enableMenuCopyRawJson && message.rawContent && (
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  message.rawContent || ""
                                );
                              }}
                              className={`${focus ? "bg-gray-100" : ""
                                } w-full text-left block px-4 py-2 text-sm text-gray-700`}
                            >
                              Copy raw JSON
                            </button>
                          )}
                        </MenuItem>
                      )}
                      {config.enableMenuShowResults && hasSearchResults && (
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              onClick={() =>
                                setIsResultsVisible(!isResultsVisible)
                              }
                              className={`${focus ? "bg-gray-100" : ""
                                } w-full text-left block px-4 py-2 text-sm text-gray-700`}
                            >
                              {isResultsVisible
                                ? "Hide results"
                                : "Show results"}
                            </button>
                          )}
                        </MenuItem>
                      )}
                      {message.requestId && config.aiChatV5Endpoint && (
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              onClick={() => downloadRequestLog(config.aiChatV5Endpoint!.replaceAll(`/ai/proxy`, `/ai/request-log/${message.requestId!}`), message.requestId!)}
                              className={`${focus ? "bg-gray-100" : ""
                                } w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                              Download Logs
                            </button>
                          )}
                        </MenuItem>
                      )}
                    </div>
                  </MenuItems>
                </Menu>
              )}
            </div>
          )}

          {/* Display Formatted Raw Content */}
          {canShowRaw && isRawVisible && (
            <pre className="mt-2 pt-2 border-t border-gray-300 text-xs bg-gray-100 p-2 rounded overflow-x-auto text-gray-600 max-h-40">
              <code>{formattedRawContent}</code>
            </pre>
          )}
        </div>
      </div>

      {/* Show your work — open the formalised reasoning trace in a modal
          (snowflake-agent). Enabled by default; only an explicit `false` (e.g.
          in a runtime JSON config) hides it, so a stale exported config can't
          suppress it. */}
      {hasReasoning && config.enableThinkingProcessReplay !== false && (
        <div className="mt-1.5 ml-0 relative z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsTraceModalOpen(true);
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors cursor-pointer"
            style={{
              color: "#0f3557",
              borderColor: "rgba(15, 53, 87, 0.4)",
            }}
            title="See how this answer was produced"
          >
            <LightBulbIcon className="h-4 w-4" />
            Show your work
          </button>
        </div>
      )}

      {isTraceModalOpen && (
        <ReasoningTraceModal
          isOpen={isTraceModalOpen}
          onClose={() => setIsTraceModalOpen(false)}
          thinkingText={thinkingMessage.thinkingText}
          trace={thinkingMessage.reasoningTrace}
        />
      )}

      {/* Follow up — focus the input to ask a follow-up (context is auto-sent).
          Hidden for client-generated (Quick Action) messages: following up on
          them yields incorrect AI results. */}
      {message.role === "assistant" && onFollowUp && !(message as ChatMessageV5).isLocal && (
        <div className="mt-1.5 ml-0 relative z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onFollowUp(String(message.content || ""));
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-600 border border-orange-400 rounded-md hover:bg-orange-50 transition-colors cursor-pointer"
            title="Reply to this answer — its quote appears above the input and the context is kept"
          >
            <ArrowUturnLeftIcon className="h-4 w-4" />
            Follow up
          </button>
        </div>
      )}

      {/* Rate Answer button for assistant messages */}
      {message.role === "assistant" && config.enableAIRating && onRateMessage && (
        !(config.useAIChatV5 && config.aiChatV5Provider === "cadviewer-analytics") ||
        !!(message as ChatMessageV5).queryLogId
      ) && (
        <div className="flex items-center gap-2 mt-1.5 ml-0 relative z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRateMessage(message);
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-600 border border-orange-400 rounded-md hover:bg-orange-50 transition-colors cursor-pointer"
          >
            Rate Answer
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRateMessage(message);
            }}
            className="p-0.5 text-gray-400 hover:text-yellow-500 transition-colors cursor-pointer"
            title="Rate this answer"
          >
            <StarIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Wing Analysis actions: highlight the wing and export the highlighted plan */}
      {message.role === "assistant" &&
        (message as any).result?.wingAnalysis &&
        wingSpaceNumbers.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-2 ml-0 relative z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleHighlightWing();
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 border border-blue-400 rounded-md hover:bg-blue-50 transition-colors cursor-pointer"
            >
              <BuildingOfficeIcon className="h-4 w-4" />
              Highlight wing on plan
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleExportWingPlan();
              }}
              disabled={isExportingPlanPdf}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {isExportingPlanPdf
                ? "Generating PDF…"
                : "Export highlighted plan (PDF)"}
            </button>
          </div>
        )}

      {/* Display Search Results or Recommendations or Combinations or Tables if they exist for this message */}
      {message.role === "assistant" && message.result && isResultsVisible && (
        <div className="mt-3 mb-2 ml-0 mr-auto pl-0 pr-4">
          <div className="relative">
            {/* Hybrid doc+sql relocation response */}
            {message.result.relocation ? (
              <div className="flex flex-col gap-2.5 pb-2 pt-0 pl-1">
                {(message.result.relocation.results || []).length > 0 ? (
                  message.result.relocation.results.map(
                    (relocResult: RelocationResult, idx: number) => (
                      <RelocationCard
                        key={`${relocResult.tenant_name}-${idx}`}
                        result={relocResult}
                        targetSpace={message.result.relocation?.target_space}
                        onHighlightSpace={handleHighlightRelocationSpace}
                        onHighlightAll={handleHighlightRelocationAll}
                      />
                    )
                  )
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    No relocation clauses found
                  </div>
                )}
              </div>
            ) : message.result.tables ? (
              <div className="flex overflow-x-auto pb-2 pt-0 pl-1 gap-2.5">
                <TablePreview
                  tableData={message.result.tables as TableData}
                  title={
                    message.result.query_interpretation?.tenant_name
                      ? `${message.result.query_interpretation.tenant_name} Data`
                      : "Query Results"
                  }
                  maxPreviewRows={3}
                  onRowClick={handleTableRowClick}
                  visualizations={message.result.visualizations}
                  mallName={message.result.query_interpretation?.mall_name || message.result.mall_name || message.result.property_name}
                  currentPlanName={selectedPlanDetail?.planName}
                  currentPropertyNumber={selectedPlan?.propertyNumber}
                  onSwitchPlan={handleSwitchPlan}
                  onHighlightAll={handleHighlightAll}
                  isHighlightActive={isTableHighlightActive}
                />
              </div>
            ) : message.result.combinations ? (
              <div className="flex overflow-x-auto pb-2 pt-0 pl-1 gap-2.5">
                {message.result.combinations.length > 0 ? (
                  message.result.combinations.map((combo: SpaceCombination, idx: number) => {
                    const fallbackKey = (combo.spaces || [])
                      .map((s) => s.space_number)
                      .filter(Boolean)
                      .join("_");
                    const key =
                      (combo.combination_id || "") +
                        (combo.rank !== undefined ? `-${combo.rank}` : "") ||
                      fallbackKey ||
                      `combo-${idx}`;
                    return (
                      <CombinationCard
                        key={key}
                        combination={combo}
                        onHighlight={handleHighlightCombination}
                        currentPlanName={selectedPlanDetail?.planName}
                        currentPropertyNumber={selectedPlan?.propertyNumber}
                        onSwitchPlan={handleSwitchPlan}
                      />
                    );
                  })
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    No combinations found
                  </div>
                )}
              </div>
            ) : message.result.recommendations ? (
              <div className="flex overflow-x-auto pb-2 pt-0 pl-1 gap-2.5">
                {message.result.recommendations.length > 0 ? (
                  message.result.recommendations.map(
                    (rec: TenantRecommendation, index: number) => (
                      <RecommendationCard
                        key={rec.tenant_name + "-" + index}
                        recommendation={rec}
                        onHighlight={handleHighlightRecommendation}
                        targetSpaceNumber={message.result.target_space?.space_number}
                        targetHighlightColor={message.result.target_space?.highlight_color}
                      />
                    )
                  )
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    No recommendations found
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Display multiple targets if available, otherwise single target */}
                {(message.result.targetPlanInfos &&
                  message.result.targetPlanInfos.length > 0) ||
                  (message.result.targetPlanInfo &&
                    (message.result.targetPlanInfo.spaceNumber ||
                      message.result.targetPlanInfo.spaceHeader)) ? (
                  <div className="mb-4">
                    {/* Multiple Targets */}
                    {message.result.targetPlanInfos &&
                      message.result.targetPlanInfos.length > 0 ? (
                      <div className="flex overflow-x-auto pb-2 pt-0 pl-1 gap-2.5">
                        {message.result.targetPlanInfos.map(
                          (
                            targetInfo: {
                              spaceNumber?: string;
                              spaceHeader?: string;
                              summary?: { gla?: string };
                              detailTabs: any[];
                            },
                            index: number
                          ) => (
                            <TargetPlanInfoCard
                              key={index}
                              spaceInfo={targetInfo as SpaceInfo}
                              onClick={() => { }}
                            />
                          )
                        )}
                      </div>
                    ) : (
                      /* Single Target */
                      message.result.targetPlanInfo &&
                      (message.result.targetPlanInfo.spaceNumber ||
                        message.result.targetPlanInfo.spaceHeader) && (
                        <div className="inline-block">
                          <TargetPlanInfoCard
                            spaceInfo={message.result.targetPlanInfo}
                            onClick={() => { }}
                          />
                        </div>
                      )
                    )}
                    {/* Arrow pointing down */}
                    <div className="flex justify-center my-0.5">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 14l-7 7m0 0l-7-7m7 7V3"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                ) : null}
                {/* Action Bar: Highlight All + View Toggles */}
                {(
                  (message.result.searchResults && message.result.searchResults.length > 0) ||
                  (message.result.targetPlanInfo) ||
                  (message.result.targetPlanInfos && message.result.targetPlanInfos.length > 0)
                ) && (
                    <div className="flex items-center justify-end mb-2 gap-2">
                      {/* Highlight All Button */}
                      <button
                        onClick={handleHighlightAll}
                        className={`flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white rounded-md transition-colors shadow-sm ${currentHighlight === message.uuid
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-green-600 hover:bg-green-700"
                          }`}
                        title={currentHighlight === message.uuid ? "Clear highlights" : "Highlight all results on canvas"}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                        </svg>
                        {currentHighlight === message.uuid ? "Clear Highlights" : "Highlight All"}
                      </button>

                      {/* View Toggles - Only for Search Results */}
                      {message.result.searchResults && message.result.searchResults.length > 0 && (
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                          <button
                            onClick={() => setIsTableView(false)}
                            className={`flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md transition-colors ${!isTableView
                              ? "bg-white text-gray-800 shadow-sm"
                              : "text-gray-500 hover:text-gray-700"
                              }`}
                            title="View as cards"
                          >
                            <Squares2X2Icon className="h-3 w-3" />
                            Cards
                          </button>
                          <button
                            onClick={() => setIsTableView(true)}
                            className={`flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md transition-colors ${isTableView
                              ? "bg-white text-gray-800 shadow-sm"
                              : "text-gray-500 hover:text-gray-700"
                              }`}
                            title="View as table"
                          >
                            <TableCellsIcon className="h-3 w-3" />
                            Table
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                {/* Cards View */}
                {!isTableView && (
                  <div className="flex overflow-x-auto pb-2 pt-0 pl-1 gap-2.5">
                    {message.result.searchResults &&
                      message.result.searchResults.length > 0 ? (
                      message.result.searchResults.map(
                        (result: SearchResultSpaceInfo, resultIndex: number) => (
                          <SearchResultCard
                            // A space number is only unique within its plan, so a
                            // portfolio-wide answer routinely repeats one (space
                            // 1590 exists in several malls). Qualify it, and fall
                            // back to the index so the key is always unique.
                            key={`${result.property_number ?? result.mall_name ?? ""}-${result.plan_name ?? ""}-${result.spaceID ?? ""}-${resultIndex}`}
                            result={result}
                            currentPlanName={selectedPlanDetail?.planName}
                            currentPropertyNumber={selectedPlan?.propertyNumber}
                            onSwitchPlan={handleSwitchPlan}
                            onClick={(clickedResult) => {
                              // Reset previous clicked space to its base color (custom or green)
                              if (
                                previousSpaceIDClicked !== undefined &&
                                previousSpaceIDClicked !== null
                              ) {
                                // Find the previous result to check its highlight_color
                                const prevResult = message.result?.searchResults?.find(
                                  (r: SearchResultSpaceInfo) => r.spaceID === previousSpaceIDClicked
                                );
                                const prevStyle = prevResult?.highlight_color
                                  ? createHighlightStyle(prevResult.highlight_color)
                                  : highlight_green;
                                cadviewer.cvjs_highlightSpace(
                                  previousSpaceIDClicked as string,
                                  prevStyle
                                );
                              }

                              setPreviousSpaceIDClicked(clickedResult.spaceID);

                              cadviewer.cvjs_hideOnlyPop();
                              if (
                                config.ShowInformativeBottomPanelWhenClickInLeftPanel
                              ) {
                                setSelectedSpaceObjectID(clickedResult.spaceID);
                              }
                              cadviewer.cvjs_highlightSpace(
                                clickedResult.spaceID as string,
                                highlight_yellow
                              );
                            }}
                          />
                        )
                      )
                    ) : (
                      <div className="text-sm text-gray-500 italic">
                        No results found
                      </div>
                    )}
                  </div>
                )}

                {/* Table View - using TablePreview for full modal support */}
                {isTableView && message.result.searchResults && message.result.searchResults.length > 0 && (() => {
                  // Get all unique field names from searchResults
                  const allFields = new Set<string>();
                  message.result.searchResults.forEach((result: any) => {
                    Object.keys(result).forEach((key) => allFields.add(key));
                  });

                  // Known field mappings for display labels
                  const fieldLabelMap: Record<string, string> = {
                    spaceID: "Space #",
                    space_number: "Space #",
                    tenant: "Tenant",
                    tenant_name: "Tenant",
                    area: "Area",
                    area_sf: "Area (sq ft)",
                    area_m2: "Area (m²)",
                    distance: "Distance",
                    distance_ft: "Distance (ft)",
                    distance_m: "Distance (m)",
                    totalRentPSF: "Rent PSF",
                    rent_per_sf: "Rent PSF",
                    rent_per_m2: "Rent (m²)",
                    leaseEndDate: "Lease End",
                    lease_end_date: "Lease End",
                    lease_start_date: "Lease Start",
                    totalRent: "Total Rent",
                    total_rent: "Total Rent",
                    total_rent_usd: "Total Rent (USD)",
                    totalSales: "Total Sales",
                    sales_r12: "Sales R12",
                    sales_r12_usd: "Sales R12 (USD)",
                    totalSalesPSF: "Sales PSF",
                    sales_per_sf: "Sales PSF",
                    mall_name: "Mall",
                    property_name: "Property",
                    plan_name: "Plan",
                    occupancy_percentage: "Occupancy %",
                    kickout_option: "Kickout Option",
                    deal_type: "Deal Type",
                    deal_stage: "Deal Stage",
                    lease_status: "Lease Status",
                    days_until_expiration: "Days to Expiry",
                    rent_remaining_usd: "Rent Remaining",
                  };

                  // Fields to exclude from display (internal/duplicate fields)
                  const excludeFields = new Set([
                    // Exclude normalized duplicates - prefer the API field names
                    'spaceID', 'tenant', 'area', 'distance', 'totalRentPSF', 'leaseEndDate',
                    'totalRent', 'totalSales', 'totalSalesPSF', 'property_name',
                  ]);

                  // Priority order for fields (fields not in this list will be sorted alphabetically at the end)
                  const fieldPriority = [
                    'space_number', 'tenant_name', 'plan_name', 'area_sf', 'area_m2',
                    'rent_per_sf', 'total_rent_usd', 'lease_end_date', 'lease_start_date',
                    'sales_r12_usd', 'occupancy_percentage', 'lease_status', 'deal_type', 'deal_stage',
                    'days_until_expiration', 'distance_ft', 'distance_m',
                  ];

                  // Filter and order fields
                  const orderedFields = Array.from(allFields)
                    .filter((field) => !excludeFields.has(field))
                    .sort((a, b) => {
                      const aIdx = fieldPriority.indexOf(a);
                      const bIdx = fieldPriority.indexOf(b);
                      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                      if (aIdx !== -1) return -1;
                      if (bIdx !== -1) return 1;
                      return a.localeCompare(b);
                    });

                  // Generate labels dynamically
                  const labels: Record<string, string> = {};
                  orderedFields.forEach((field) => {
                    labels[field] = fieldLabelMap[field] ||
                      // Convert snake_case to Title Case for unknown fields
                      field.split('_').map(word =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ');
                  });

                  // Convert searchResults to TableData format with all fields
                  const tableData: TableData = {
                    labels,
                    data: message.result.searchResults.map((result: any) => {
                      const rowData: Record<string, any> = {};
                      orderedFields.forEach((field) => {
                        rowData[field] = result[field] ?? "-";
                      });
                      // Always include space_number for click highlighting
                      rowData.space_number = result.space_number || result.spaceID;
                      // Include plan_name for switch plan functionality
                      rowData.plan_name = result.plan_name;
                      return rowData;
                    }),
                  };

                  return (
                    <TablePreview
                      tableData={tableData}
                      title="Search Results"
                      maxPreviewRows={5}
                      onRowClick={handleTableRowClick}
                      currentPlanName={selectedPlanDetail?.planName}
                      currentPropertyNumber={selectedPlan?.propertyNumber}
                      onSwitchPlan={handleSwitchPlan}
                      onHighlightAll={handleHighlightAll}
                      isHighlightActive={currentHighlight === message.uuid}
                    />
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessageDisplay;
